import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { useSelectItemsTranslate } from "@ryde/redux";
import {
  BulkProduceTypeEnum,
  IConflict,
  IHubBookingsPreviewRow,
  IHubBookingsPreviewSummary,
  IProduceJoinDto,
  IProductionCourseSchedulePreview,
  IProductionGeneralInfo,
  IProductionSummaryItem,
  productionUiDisplayStateSelector,
} from "@ryde/shared";

import {
  Conflicts,
  resolveConflictNavSeverity,
  useConflicts,
} from "../../../conficts";
import { GroupTitleTemplate } from "../../../dynamic-forms";
import {
  GeneralInfoBlock,
  GeneralInfoTextBlock,
  GeneralInfoTitleBlock,
  ProductionCourseSchedulePreview,
  ProductionHubBookingsPreview,
  ProductionPreviewProgress,
  ProductionPreviewResult,
  ProductionPreviewWrapperStyled,
} from "../components";
import { ProductionGeneralInfoBlock } from "./production-general-info-block";
import { ProductionPreviewDetailsSummaryItems } from "./production-preview-details.summary-items";

interface IProductionPreviewDetailsProps {
  isStarted: boolean;
  handleProductionAbort: () => void;
  isOtherProductionInProgress: boolean;
  handlePreviousProductionClick: () => void;
  conflicts: IConflict[];
  loadProductionPreview: () => Promise<void>;
  groupedConflicts: {
    [index: string]: IConflict[];
  };
  productionPayload: IProduceJoinDto;
  generalInfo?: IProductionGeneralInfo;
  summaryItems?: IProductionSummaryItem[];
  isAborting?: boolean;
  courseSchedulePreviews?: IProductionCourseSchedulePreview[];
  hubBookingsPreview?: IHubBookingsPreviewRow[];
  hubBookingsPreviewSummary?: IHubBookingsPreviewSummary;
  hubAutoAssignedBookingIds?: number[];
  hubBookingsExcludedWithoutHubTypeIds?: number[];
}

export const ProductionPreviewDetails: FC<IProductionPreviewDetailsProps> = ({
  isStarted,
  handleProductionAbort,
  isOtherProductionInProgress,
  handlePreviousProductionClick,
  conflicts,
  loadProductionPreview,
  groupedConflicts,
  productionPayload,
  generalInfo,
  summaryItems,
  isAborting = false,
  courseSchedulePreviews,
  hubBookingsPreview,
  hubBookingsPreviewSummary,
  hubAutoAssignedBookingIds,
  hubBookingsExcludedWithoutHubTypeIds,
}) => {
  const { t } = useTranslation();
  const { translateLabel } = useSelectItemsTranslate();
  const { getConflictsHeaders } = useConflicts();

  const { productionId } = useRecoilValue(productionUiDisplayStateSelector);

  const productionActionLabel = useMemo(
    () =>
      productionPayload?.action
        ? t(`booking.generalInfo.${productionPayload.action}`)
        : "",
    [productionPayload?.action, t],
  );

  const productionTypesLabel = useMemo(() => {
    const types = productionPayload?.productionType;

    if (!types?.length) {
      return "";
    }

    return types.map(type => translateLabel(type)).join(", ");
  }, [productionPayload?.productionType, translateLabel]);

  return (
    <ProductionPreviewWrapperStyled>
      <ProductionPreviewProgress
        isStarted={isStarted}
        isAborting={isAborting}
        isOtherProductionInProgress={isOtherProductionInProgress}
        handleProductionAbort={handleProductionAbort}
        handlePreviousProductionClick={handlePreviousProductionClick}
      />
      <ProductionPreviewResult
        isStarted={isStarted}
        action={productionPayload.action}
      />

      {!!conflicts?.length && (
        <GroupTitleTemplate
          name="preview"
          formLabel={t("rideProduction.note")}
        />
      )}
      {Object.keys(groupedConflicts).map(conflictGroupKey => (
        <Conflicts
          key={conflictGroupKey}
          title={t(`booking.formLabel.conflictsDetails.${conflictGroupKey}`)}
          conflicts={groupedConflicts[conflictGroupKey]}
          loadProductionPreview={loadProductionPreview}
          headers={getConflictsHeaders(conflictGroupKey)}
          conflictNavSeverity={resolveConflictNavSeverity(conflictGroupKey)}
        />
      ))}
      <GroupTitleTemplate
        name="generalInfo"
        formLabel={t("rideProduction.generalInfo")}
      />
      {Boolean(productionId) && (
        <GeneralInfoBlock>
          <GeneralInfoTitleBlock>
            {t("ride.PRODUCTION_ID")}
          </GeneralInfoTitleBlock>
          <GeneralInfoTextBlock>{productionId}</GeneralInfoTextBlock>
        </GeneralInfoBlock>
      )}
      <GeneralInfoBlock>
        <GeneralInfoTitleBlock>
          {t("rideProduction.metricsFilters.action")}
        </GeneralInfoTitleBlock>
        <GeneralInfoTextBlock>{productionActionLabel}</GeneralInfoTextBlock>
      </GeneralInfoBlock>
      {Boolean(productionTypesLabel) && (
        <GeneralInfoBlock>
          <GeneralInfoTitleBlock>
            {t("rideProduction.metricsFilters.productionType")}
          </GeneralInfoTitleBlock>
          <GeneralInfoTextBlock>{productionTypesLabel}</GeneralInfoTextBlock>
        </GeneralInfoBlock>
      )}
      <GeneralInfoBlock>
        <GeneralInfoTitleBlock>
          {t("booking.generalInfo.summary")}
        </GeneralInfoTitleBlock>
        <ProductionPreviewDetailsSummaryItems summaryItems={summaryItems} />
      </GeneralInfoBlock>
      {generalInfo &&
        Object.keys(generalInfo).map(key => (
          <ProductionGeneralInfoBlock
            title={key}
            key={key}
            items={generalInfo[key]}
          />
        ))}
      <ProductionCourseSchedulePreview
        courseSchedulePreviews={courseSchedulePreviews}
      />
      <ProductionHubBookingsPreview
        hubBookingsPreview={hubBookingsPreview}
        hubBookingsPreviewSummary={hubBookingsPreviewSummary}
        hubAutoAssignedBookingIds={hubAutoAssignedBookingIds}
        hubBookingsExcludedWithoutHubTypeIds={
          hubBookingsExcludedWithoutHubTypeIds
        }
        showJoinExcludedWarning={
          productionPayload.action === BulkProduceTypeEnum.Join
        }
      />
    </ProductionPreviewWrapperStyled>
  );
};
